import { observable, action } from "mobx";
import Utils from "Utils/utils";
import DateUtils from "Utils/DateUtils";
import CallsUtils from "Utils/CallsUtils";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { SortParamObj } from "./constants";
import { HEADERS } from "./constants";

let cancelRequest = null;
let cancelAnalyticsRequest = null;

const checkAndCancelRequest = () => {
  if (cancelRequest !== null) {
    cancelRequest("cancel");
    cancelRequest = null;
  }
};

const checkAndCancelAnalyticsRequest = () => {
  if (cancelAnalyticsRequest !== null) {
    cancelAnalyticsRequest("cancel");
    cancelAnalyticsRequest = null;
  }
};

const cancelApiRequest = value => {
  cancelRequest = value;
};

const cancelAnalyticsApiRequest = value => {
  cancelAnalyticsRequest = value;
};

class ReportsStore {
  @observable reportsData = [];
  @observable dateFilter = Utils.getDateWithTimestamp(
    Utils.getFromToDateWithTimeZoneByDaysCount(29)
  );
  @observable userheadersId = "";
  @observable teamHeaderId = "";
  @observable sequenceHeaderId = "";
  @observable tabType = "";
  @observable loading = false;
  @observable openDragAndDrop = false;
  @observable saveLoader = null;
  @observable pageNo = 1;
  @observable totalPage = 1;
  @observable pageLimit = 10;
  @observable searchText = "";
  @observable export = false;
  @observable headers = [...HEADERS];
  @observable sortParam = "contactsAddedCount";
  @observable sortType = "ASC";
  @observable memberIds = null;
  @observable newVersionReportsFlag = false;
  @observable activeDateFilter = "month";
  @observable sequenceAnalyticsData = [];
  @observable callsAnalyticsData = [];
  @observable callsOutcomeData = [];
  @observable linkedInAnalyticsData = [];
  @observable whatsAppAnalyticsData = {};

  @action
  setActiveDateFilter = (filter = "") => {
    this.activeDateFilter = filter;
  };

  @action
  setSequenceAnalyticsData = (data = []) => {
    this.sequenceAnalyticsData = data;
  };

  @action
  setCallsAnalyticsData = (data = []) => {
    this.callsAnalyticsData = data;
  };

  @action
  setCallsOutcomeData = (data = []) => {
    this.callsOutcomeData = data;
  };


  @action
  setLinkedInAnalyticsData = (data = []) => {
    this.linkedInAnalyticsData = data;
  };

  @action
  setWhatsAppAnalyticsData = (data = {}) => {
    this.whatsAppAnalyticsData = data;
  };

  @observable callsReportData = {
      overview: {
          totalCalls: 0,
          loggedCalls: 0,
          notLoggedCalls: 0,
          totalTimeOnCall: "",
          avgCallDuration: ""
      },
      outcomes: {
          answered: [],
          notAnswered: []
      }
  };
  @observable callsAnalyticsData = [];
  @observable callsTeamSummaryData = [];
  @observable callsAnalyticsLoader = false;
  @observable callsTeamSummaryLoader = false;

  @action
  setCallsReportData = (data = {}) => {
      // Update properties individually to ensure MobX tracks changes properly
      const overview = data.overview || {};
      const outcomes = data.outcomes || {};
      
      // Update overview properties
      this.callsReportData.overview.totalCalls = overview.totalCalls ?? 0;
      this.callsReportData.overview.loggedCalls = overview.loggedCalls ?? 0;
      this.callsReportData.overview.notLoggedCalls = overview.notLoggedCalls ?? 0;
      this.callsReportData.overview.totalTimeOnCall = overview.totalTimeOnCall !== undefined ? CallsUtils.secondsToTime(overview.totalTimeOnCall) : "";
      
      const avgDuration = overview.averageCallDuration ?? overview.avgCallDuration ?? overview.avgCallDurationInSecs;
      this.callsReportData.overview.avgCallDuration = avgDuration !== undefined ? CallsUtils.secondsToTime(avgDuration) : "";
      
      // Replace arrays to ensure MobX tracks the changes
      this.callsReportData.outcomes.answered = Array.isArray(outcomes.answered) ? [...outcomes.answered] : [];
      this.callsReportData.outcomes.notAnswered = Array.isArray(outcomes.notAnswered) ? [...outcomes.notAnswered] : [];
  };

  @action
  fetchCallsReportV2 = async () => {
      const config = {
          url: URLS.getCallsReportV2,
          method: "POST",
          data: {
              fromDate: this.dateFilter?.fromDate,
              toDate: this.dateFilter?.toDate,
              memberIds: this.memberIds || []
          }
      };
      const response = await makeApi(config, cancelAnalyticsApiRequest);
      checkAndCancelAnalyticsRequest();
      const { data = {}, status = "" } = response || {};
      if (+status === 200) {
          // setCallsReportData will handle formatting and updating observables
          data&& data.data && this.setCallsReportData(data.data || {});
      }
  };

  @action
  fetchCallsActivity = async () => {
      this.setLoading(true);
      
      // Convert timestamps to local date strings if needed
      let fromDate = "";
      let toDate = "";
      
      if (this.dateFilter?.fromDate) {
        if (typeof this.dateFilter.fromDate === 'string') {
          fromDate = this.dateFilter.fromDate;
        } else {
          const dateObj = new Date(this.dateFilter.fromDate);
          fromDate = Utils.convertFromDateToLocalString(dateObj);
        }
      }
      
      if (this.dateFilter?.toDate) {
        if (typeof this.dateFilter.toDate === 'string') {
          toDate = this.dateFilter.toDate;
        } else {
          const dateObj = new Date(this.dateFilter.toDate);
          toDate = Utils.convertToDateToLocalString(dateObj);
        }
      }

      const tempPayload = {
          pageNo: this?.pageNo || 1,
          limit: this?.pageLimit,
          sortParam: SortParamObj[this?.sortParam],
          direction: this?.sortType,
          fromDate: fromDate,
          toDate: toDate,
          memberIds: this.memberIds || []
      };
      const config = {
          url: URLS.getCallsActivity,
          method: "POST",
          data: tempPayload
      };
      const response = await makeApi(config, cancelApiRequest);
      checkAndCancelRequest();
      const { data = {}, status = "" } = response || {};
      if (+status === 200) {
          const callsActivityData = data?.callsActivityReport || [];
          this.setReportsData(callsActivityData);
          const totalPages = Math.ceil(callsActivityData?.length / this?.pageLimit);
          this.setTotalPage(totalPages);
      }
      this.setLoading(false);
  };

  @action
  setNewVersionReportsFlag = (flag = false) => {
    this.newVersionReportsFlag = flag;
  };

  @action
  setUserHeadersId = (id = "") => {
    this.userheadersId = id;
  };

  @action
  setTeamHeaderId = (id = "") => {
    this.teamHeaderId = id;
  };

  @action
  setSequenceHeaderId = (id = "") => {
    this.sequenceHeaderId = id;
  };

  @action
  setMemberIds = (ids = []) => {
    this.memberIds = ids;
  };

  @action
  setDateFilter = (date = {}) => {
    this.dateFilter = date;
  };

  @action
  setReportsData = (val = {}) => {
    this.reportsData = val;
  };

  @action
  setTabType = (type = "") => {
    this.tabType = type;
  };

  @action
  setLoading = (loading = false) => {
    this.loading = loading;
  };

  @action
  setOpenDragAndDrop = (value = false) => {
    this.openDragAndDrop = value;
  };

  @action
  setSaveLoader = (saveLoader = false) => {
    this.saveLoader = saveLoader;
  };

  @action
  setPageNo = (pageNo = 0) => {
    this.pageNo = pageNo;
  };

  @action
  setTotalPage = (totalPage = 0) => {
    this.totalPage = totalPage;
  };

  @action
  setPageLimit = (limit = 10) => {
    this.pageLimit = limit;
  };

  @action
  setSearchText = (text = "") => {
    this.searchText = text;
  };

  @action
  setExport = (exportVal = "") => {
    this.export = exportVal;
  };

  @action
  setHeaders = (headersVal = []) => {
    this.headers = headersVal;
  };

  @action
  setSortParam = (param = "") => {
    this.sortParam = param;
  };

  @action
  setSortType = (type = "") => {
    this.sortType = type;
  };

  @action
  transformedGroupReport = (groupReportData = []) => {
    return groupReportData.map(item => {
      const { report, group } = item;
      return {
        ...report,
        ...group,
        user: {
          memberId: group?.memberId,
          firstName: group?.name
        }
      };
    });
  };

  @action
  fetchReportsData = async (payload = {}, type = "", cbk = () => {}) => {
    const tempPayload = {
      pageNo: this?.pageNo || 1,
      limit: this?.pageLimit,
      sortParam: SortParamObj[this?.sortParam],
      direction: this?.sortType,
      fromDate: this.dateFilter?.fromDate,
      toDate: this.dateFilter?.toDate,
      memberIds: this.memberIds || []
    };
    const URLObj = {
      user: URLS.getUserReports,
      team: URLS.getGroupReports,
      sequence: URLS.getSequenceReports
    };
    const config = {
      url: URLObj[type?.toLowerCase()],
      method: "POST",
      data: tempPayload
    };
    const response = await makeApi(config, cancelApiRequest);
    checkAndCancelRequest();
    const { data = {}, status = "" } = response || {};
    if (+status === 200) {
      let tempData =
        type === "user"
          ? data?.userReport
          : type === "sequence"
          ? data?.sequenceReport
          : this.transformedGroupReport(data?.groupReport);
      this.setReportsData(tempData || []);
      let totalPages = Math.ceil(tempData?.length / 10);
      this.setTotalPage(totalPages);
    }
  };

  @action
  fetchSequenceAnalytics = async () => {
    const tempPayload = {
      fromDate: this.dateFilter?.fromDate,
      toDate: this.dateFilter?.toDate,
      memberIds: this.memberIds || []
    };
    const config = {
      url: URLS.getSequenceAnalytics,
      method: "POST",
      data: tempPayload
    };
    const response = await makeApi(config, cancelAnalyticsApiRequest);
    checkAndCancelAnalyticsRequest();
    const { data = {}, status = "" } = response || {};
    if (+status === 200) {
      reportsStore.setSequenceAnalyticsData(data);
    }
  };

  @action
  fetchCallsAnalytics = async () => {
    this.callsAnalyticsLoader = true;
    // Convert timestamps to local date strings for analytics API
    // The dateFilter contains timestamps, but analytics API expects local date strings
    let fromDate = "";
    let toDate = "";
    
    if (this.dateFilter?.fromDate) {
      // If it's already a string, use it; otherwise convert from timestamp
      if (typeof this.dateFilter.fromDate === 'string') {
        fromDate = this.dateFilter.fromDate;
      } else {
        // Convert timestamp to Date, then to local string
        const dateObj = new Date(this.dateFilter.fromDate);
        fromDate = Utils.convertFromDateToLocalString(dateObj);
      }
    }
    
    if (this.dateFilter?.toDate) {
      // If it's already a string, use it; otherwise convert from timestamp
      if (typeof this.dateFilter.toDate === 'string') {
        toDate = this.dateFilter.toDate;
      } else {
        // Convert timestamp to Date, then to local string
        const dateObj = new Date(this.dateFilter.toDate);
        toDate = Utils.convertToDateToLocalString(dateObj);
      }
    }
    
    // Format payload to match what the analytics endpoint expects
    const filter = {
      fromDate,
      to: toDate,
      timeZone: Utils.getCurrentUserTimeZone()
    };
    if (this.memberIds && this.memberIds.length > 0) {
      filter.memberIds = this.memberIds;
    }
    const config = {
      url: URLS.getAnalyticsData,
      method: "POST",
      data: { filter }
    };
    const response = await makeApi(config, cancelAnalyticsApiRequest);
    checkAndCancelAnalyticsRequest();
    const { data = {}, status = "" } = response || {};
    if (+status === 200) {
      this.setCallsAnalyticsData(data?.results || []);
    }
    this.callsAnalyticsLoader = false;
  };

  @action
  fetchCallsTeamSummaryAnalytics = async () => {
    this.callsTeamSummaryLoader = true;
    // Convert timestamps to local date strings for analytics API
    let fromDate = "";
    let toDate = "";
    
    if (this.dateFilter?.fromDate) {
      // If it's already a string, use it; otherwise convert from timestamp
      if (typeof this.dateFilter.fromDate === 'string') {
        fromDate = this.dateFilter.fromDate;
      } else {
        // Convert timestamp to Date, then to local string
        const dateObj = new Date(this.dateFilter.fromDate);
        fromDate = Utils.convertFromDateToLocalString(dateObj);
      }
    }
    
    if (this.dateFilter?.toDate) {
      // If it's already a string, use it; otherwise convert from timestamp
      if (typeof this.dateFilter.toDate === 'string') {
        toDate = this.dateFilter.toDate;
      } else {
        // Convert timestamp to Date, then to local string
        const dateObj = new Date(this.dateFilter.toDate);
        toDate = Utils.convertToDateToLocalString(dateObj);
      }
    }
    
    // Format payload to match what the team analytics endpoint expects
    const filter = {
      fromDate,
      to: toDate,
      timeZone: Utils.getCurrentUserTimeZone()
    };
    if (this.memberIds && this.memberIds.length > 0) {
      filter.memberIds = this.memberIds;
    }
    const config = {
      url: URLS.getAnalyticsTeamData,
      method: "POST",
      data: { filter }
    };
    const response = await makeApi(config, cancelAnalyticsApiRequest);
    checkAndCancelAnalyticsRequest();
    const { data = {}, status = "" } = response || {};
    if (+status === 200) {
      this.setCallsTeamSummaryData(data?.results || []);
    }
    this.callsTeamSummaryLoader = false;
  };

  @action
  setCallsTeamSummaryData = (data = []) => {
    this.callsTeamSummaryData = data;
  };

  @action
  fetchCallsOutcome = async () => {
    const tempPayload = {
      fromDate: this.dateFilter?.fromDate,
      toDate: this.dateFilter?.toDate,
      memberIds: this.memberIds || []
    };
    const config = {
      url: URLS.getCallsOutcomeFilterValue,
      method: "POST",
      data: tempPayload
    };
    const response = await makeApi(config, cancelAnalyticsApiRequest);
    // checkAndCancelAnalyticsRequest(); // Might interfere if called in parallel?
    // Let's assume it's fine or manage separate cancel token if needed.
    // For now, reusing the same cancel token might be risky if they are parallel.
    // But reportsStore usually cancels previous request before new one.
    // If I call both in parallel, the first one might be cancelled.
    // I should probably not share the same cancel token variable if I want parallel.
    // But `fetchReportsData` and `fetchSequenceAnalytics` use different tokens.
    // `cancelAnalyticsApiRequest` is used for `fetchSequenceAnalytics`.
    // I'll use `cancelAnalyticsApiRequest` for now, but be aware of race conditions.
    // Actually, `checkAndCancelAnalyticsRequest` cancels the *previous* request.
    // If I fire two requests, the second might cancel the first.
    // I should probably chain them or use Promise.all in the component.
    
    const { data = {}, status = "" } = response || {};
    if (+status === 200) {
      reportsStore.setCallsOutcomeData(data?.counts || []);
    }
  };

  @action
  fetchLinkedInAnalytics = async () => {
    const tempPayload = {
      fromDate: this.dateFilter?.fromDate,
      toDate: this.dateFilter?.toDate,
      memberIds: this.memberIds || []
    };
    const config = {
      url: URLS.getLinkedInAnalytics,
      method: "POST",
      data: tempPayload
    };
    const response = await makeApi(config, cancelAnalyticsApiRequest);
    checkAndCancelAnalyticsRequest();
    const { data = {}, status = "" } = response || {};
    if (+status === 200 && data && data.data) {
      this.setLinkedInAnalyticsData(data.data);
    }
  };

  @action
  fetchWhatsAppAnalytics = async () => {
    const tempPayload = {
      fromDate: this.dateFilter?.fromDate,
      toDate: this.dateFilter?.toDate,
      memberIds: this.memberIds || []
    };
    const config = {
      url: URLS.getWhatsAppAnalytics,
      method: "POST",
      data: tempPayload
    };
    const response = await makeApi(config, cancelAnalyticsApiRequest);
    checkAndCancelAnalyticsRequest();
    const { data = {}, status = "" } = response || {};
    if (+status === 200 && data && data.data) {
      this.setWhatsAppAnalyticsData(data.data);
    }
  };

  @action
  fetchHeaderList = async (type = "REPORTS_USER_ACTIVITY", cbk = () => {}) => {
    const config = {
      url: `${URLS.getTableHeaders}${type}`,
      method: "GET"
    };
    const response = await makeApi(config);
    const { data = {} } = response || {};
    this.setDragAndDropTableId(data?.id, type);
    let filteredColumns = data?.columns.filter(column => {
      const displayName = column.displayName.toLowerCase();
      return !["user", "users", "team", "teams", "sequence name"].includes(
        displayName?.toLowerCase()
      );
    });
    cbk(filteredColumns);
  };

  @action
  saveDynamicHeaders = async (
    type = "REPORTS_USER_ACTIVITY",
    cbk = () => {},
    headerArr = []
  ) => {
    const config = {
      url: URLS.editTableHeaders,
      method: "POST",
      data: {
        id:
          type?.toUpperCase() === "REPORTS_USER_ACTIVITY"
            ? this.userheadersId
            : type?.toUpperCase() === "REPORTS_TEAM_ACTIVITY"
            ? this.teamHeaderId
            : this.sequenceHeaderId,
        tableName: type,
        columns: headerArr
      }
    };
    const response = await makeApi(config);
    const { data = {} } = response || {};
    if (data?.columns) {
      this.setDragAndDropTableId(data?.id, type);
      let filteredColumns = data?.columns.filter(column => {
        const displayName = column?.displayName?.toLowerCase();
        return !["user", "users", "team", "teams", "sequence name"].includes(
          displayName?.toLowerCase()
        );
      });
      cbk(filteredColumns);
      toasterState.setToastMsg(
        "Table column order has been updated successfully",
        "success"
      );
    }
  };

  @action
  setDragAndDropTableId = (id = "", type = "") => {
    return type?.toUpperCase() === "REPORTS_USER_ACTIVITY"
      ? this.setUserHeadersId(id)
      : type?.toUpperCase() === "REPORTS_TEAM_ACTIVITY"
      ? this.setTeamHeaderId(id)
      : this.setSequenceHeaderId(id);
  };

  @action
  resetReports = () => {
    this.tabType = "";
    this.pageNo = 1;
    this.pageLimit = 10;
    this.sortParam = "contactsAddedCount";
    this.sortType = "ASC";
    this.reportsData = [];
  };
}

const reportsStore = new ReportsStore();
export { reportsStore };
export default reportsStore;
